import React, { useState, useEffect } from 'react';
import { 
  getProductTransactions, 
  getInventoryMovementSummary 
} from '../../services/inventoryService';
import { 
  InventoryTransaction, 
  InventoryTransactionsResponse, 
  InventoryMovementSummaryResponse 
} from '../../types/inventory';
import './InventoryHistory.css';

interface InventoryHistoryProps {
  productId: string;
  productName: string;
  variantId?: string;
}

const InventoryHistory: React.FC<InventoryHistoryProps> = ({
  productId,
  productName,
  variantId
}) => {
  // State for transactions and summary
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [summary, setSummary] = useState<InventoryMovementSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Fetch transactions on component mount and when page changes
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch transactions for this product/variant
        const data = await getProductTransactions(productId, variantId, page, 10);
        setTransactions(data.transactions);
        setTotalPages(data.totalPages);
        
        // Fetch movement summary
        if (page === 1) {
          const summaryData = await getInventoryMovementSummary(productId);
          setSummary(summaryData);
        }
      } catch (err: any) {
        console.error('Error fetching inventory history:', err);
        setError(err.message || 'فشل في تحميل سجل المخزون');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTransactions();
  }, [productId, variantId, page]);
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('ar-EG', options);
  };
  
  // Translate transaction type to Arabic
  const translateTransactionType = (type: string) => {
    const types: Record<string, string> = {
      'addition': 'إضافة',
      'subtraction': 'خصم',
      'transfer': 'تحويل',
      'adjustment': 'تعديل'
    };
    return types[type] || type;
  };
  
  // Format transaction quantity with sign
  const formatQuantity = (transaction: InventoryTransaction) => {
    const quantity = Math.abs(transaction.quantity);
    
    switch (transaction.type) {
      case 'addition':
        return `+${quantity}`;
      case 'subtraction':
        return `-${quantity}`;
      case 'transfer':
        // For transfers, show negative on source warehouse and positive on destination
        if (transaction.destinationWarehouse) {
          return `-${quantity}`;
        } else {
          return `+${quantity}`;
        }
      case 'adjustment':
        return transaction.quantity >= 0 ? `+${quantity}` : `-${quantity}`;
      default:
        return quantity.toString();
    }
  };
  
  // Get CSS class for transaction type
  const getTransactionClass = (transaction: InventoryTransaction) => {
    switch (transaction.type) {
      case 'addition':
        return 'addition';
      case 'subtraction':
        return 'subtraction';
      case 'transfer':
        return transaction.destinationWarehouse ? 'transfer-out' : 'transfer-in';
      case 'adjustment':
        return transaction.quantity >= 0 ? 'adjustment-up' : 'adjustment-down';
      default:
        return '';
    }
  };
  
  // Render movement summary if available
  const renderSummary = () => {
    if (!summary) return null;
    
    const currentVariantSummary = summary.variantSummaries.find(v => 
      v.variantId === (variantId || 'default')
    );
    
    if (!currentVariantSummary) return null;
    
    return (
      <div className="inventory-summary">
        <h4>ملخص حركة المخزون</h4>
        <div className="summary-grid">
          {currentVariantSummary.movements.addition && (
            <div className="summary-item addition">
              <div className="summary-value">+{currentVariantSummary.movements.addition.totalQuantity}</div>
              <div className="summary-label">إضافات</div>
              <div className="summary-count">{currentVariantSummary.movements.addition.count} معاملة</div>
            </div>
          )}
          
          {currentVariantSummary.movements.subtraction && (
            <div className="summary-item subtraction">
              <div className="summary-value">-{currentVariantSummary.movements.subtraction.totalQuantity}</div>
              <div className="summary-label">مسحوبات</div>
              <div className="summary-count">{currentVariantSummary.movements.subtraction.count} معاملة</div>
            </div>
          )}
          
          {currentVariantSummary.movements.transfer && (
            <div className="summary-item transfer-out">
              <div className="summary-value">{currentVariantSummary.movements.transfer.totalQuantity}</div>
              <div className="summary-label">تحويلات</div>
              <div className="summary-count">{currentVariantSummary.movements.transfer.count} معاملة</div>
            </div>
          )}
          
          {currentVariantSummary.movements.adjustment && (
            <div className="summary-item adjustment-up">
              <div className="summary-value">{currentVariantSummary.movements.adjustment.totalQuantity}</div>
              <div className="summary-label">تعديلات</div>
              <div className="summary-count">{currentVariantSummary.movements.adjustment.count} معاملة</div>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Handle pagination
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  
  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };
  
  if (loading && transactions.length === 0) {
    return <div className="loading-container">جاري تحميل سجل المخزون...</div>;
  }
  
  if (error && transactions.length === 0) {
    return <div className="error-container">{error}</div>;
  }
  
  return (
    <div className="inventory-history">
      <h3 className="section-title">سجل حركة المخزون</h3>
      
      {renderSummary()}
      
      {transactions.length === 0 ? (
        <div className="no-transactions">لا توجد معاملات مخزون لهذا المنتج</div>
      ) : (
        <>
          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>نوع المعاملة</th>
                  <th>المستودع</th>
                  <th>الكمية</th>
                  <th>الكمية الجديدة</th>
                  <th>ملاحظات</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(transaction => (
                  <tr key={transaction._id} className={getTransactionClass(transaction)}>
                    <td>{formatDate(transaction.transactionDate)}</td>
                    <td>
                      {translateTransactionType(transaction.type)}
                      {transaction.type === 'transfer' && transaction.destinationWarehouseName && (
                        <span className="transfer-direction">
                          إلى {transaction.destinationWarehouseName}
                        </span>
                      )}
                      {transaction.type === 'transfer' && !transaction.destinationWarehouseName && (
                        <span className="transfer-direction">
                          من {transaction.warehouseName}
                        </span>
                      )}
                    </td>
                    <td>{transaction.warehouseName}</td>
                    <td className="quantity-cell">{formatQuantity(transaction)} {transaction.unitName}</td>
                    <td>{transaction.newQuantity} {transaction.unitName}</td>
                    <td>{transaction.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button 
                onClick={handlePrevPage} 
                disabled={page === 1}
                className="pagination-button"
              >
                السابق
              </button>
              <span className="pagination-info">
                صفحة {page} من {totalPages}
              </span>
              <button 
                onClick={handleNextPage} 
                disabled={page === totalPages}
                className="pagination-button"
              >
                التالي
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InventoryHistory;